import { Injectable } from '@angular/core';
import {
    areEqual,
    DwAttributeType,
    DwContextDefinition,
    DwControlType,
    DwCustomContextBase,
    DwCustomContextDefinition,
    DwOrmDataService,
    DwSecurityTopics,
    DwSecurityUserService
} from '@devwareapps/devware-cap';
import { AppMetaDataItemNames, AppMetaDataLookups } from '../../../meta-data/app-meta-data.service';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { SchoolRepositoryService } from '../services/school-repository.service';
import { SUBSCRIPTION_CHANGED_TOPC } from '../../payments/services/payments-repository.service';
import { SchoolFeatures } from '../models/school-features.model';


export const SchoolFeatureContext = 'SchoolFeatureContext';
@DwCustomContextDefinition({
    keyName: SchoolFeatureContext,
    display: 'School Features',
    isGlobal: true,
})
@Injectable()
export class SchoolFeatureContextService extends DwCustomContextBase {
    private previousFeatures: SchoolFeatures;

    constructor(
        private dwSecurityUserService: DwSecurityUserService,
        private schoolRepositoryService: SchoolRepositoryService
    ) {
        super();

        this.dwSecurityUserService.securityContextChanged()
            .subscribe(() => this.previousFeatures = null); 
    }

    setupContextDefinition(contextDef: DwContextDefinition) {
        contextDef.loadContext = (context) => this.getCustomContext(context);

        contextDef.cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];

        contextDef.cacheInvalidationTopics.push(SUBSCRIPTION_CHANGED_TOPC);
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Subscription));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchool));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchoolCourse));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchoolPackage));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchoolRegistrationLink));
        contextDef.cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.FlightSchoolRegistrationEmailDomain));
        
        contextDef.attributes = [];
        // SchoolRepositoryService owns the request cache. Avoid a second context cache so
        // a refresh can emit the previous value before the replacement request completes.
        contextDef.noCache = true;

        this.addProperty(contextDef.attributes, "FlightSchoolId", "Flight School id", DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, "FlightSchoolName", "Flight School Name", DwAttributeType.Text, DwControlType.Textbox);
        this.addProperty(contextDef.attributes, "FlightSchoolUrl", "Flight School Url", DwAttributeType.Text, DwControlType.Textbox);
        this.addProperty(contextDef.attributes, "FlightSchoolRegistrationUrl", "Registration Link", DwAttributeType.Text, DwControlType.Textbox);
        this.addProperty(contextDef.attributes, "FlightSchoolInstructorRegistrationUrl", "Instructor Registration Link", DwAttributeType.Text, DwControlType.Textbox);
        this.addProperty(contextDef.attributes, "SchoolBillingModeId", "School Billing Mode", DwAttributeType.Integer,
            DwControlType.DropDownList, AppMetaDataLookups.SchoolBillingModeAllItems);

        this.addProperty(contextDef.attributes, "SubscriptionTierId", "Subscription Tier Id", DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, "SubscriptionTierName", "Subscription Tier Name", DwAttributeType.Text, DwControlType.Textbox);
        this.addProperty(contextDef.attributes, "IsMarketplaceEnabled", "Is Marketplace Enabled", DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, "StudentProfitSharingReductionEnabled", "Student Profit Sharing Reduction Enabled", DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, "CourseCount", "Course Count", DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, "PackageCount", "Package Count", DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, "Features", "Features", DwAttributeType.Integer, DwControlType.DropDownList, AppMetaDataLookups.SubscriptionFeatureAllItems);
        this.addProperty(contextDef.attributes, "SubscriptionTierRoleIds", "Subscription Tier Roles", DwAttributeType.Integer,
            DwControlType.DropDownList, AppMetaDataLookups.SubscriptionRoleAllItems);
        this.addProperty(contextDef.attributes, "SchoolFeatures", "School Features", DwAttributeType.Integer, DwControlType.DropDownList, AppMetaDataLookups.SchoolFeatureAllItems);

        this.addProperty(contextDef.attributes, "HasDefaultPaymentMethod", "Has Default Payment", DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, "FlightSchoolCreatedDateTime", "Flight School Created", DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, "FlightSchoolModifiedDateTime", "Flight School MOdified", DwAttributeType.YesNo, DwControlType.Checkbox);
        this.addProperty(contextDef.attributes, "FlightSchoolThemeId", "Flight School Theme", DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(contextDef.attributes, "ProcessorConfigId", "Processor Config Id", DwAttributeType.Integer, DwControlType.NumericTextBox);
    }

    getCustomContext(_context: any): Observable<SchoolFeatures> {
        const previousFeatures = this.previousFeatures;
        let features$ = this.schoolRepositoryService.getSchoolFeatures()
            .pipe(map(features => {
                if (features?.FlightSchoolId) {
                    this.previousFeatures = features;
                    return features;
                }

                return this.previousFeatures || features;
            }));

        if (previousFeatures) {
            features$ = features$.pipe(
                startWith(previousFeatures),
                distinctUntilChanged((previous, current) => areEqual(previous, current))
            );
        }

        return features$;
    }
}
